import { MailCircularProgress } from "@/features/shared/MailCircularProgress";
import { cn } from "@/lib/cn";

export type MailLoadingVariant =
  | "environment"
  | "webmail"
  | "messages"
  | "search"
  | "folders"
  | "message"
  | "sync";

const VARIANT_COPY: Record<
  MailLoadingVariant,
  { title: string; hint?: string }
> = {
  environment: {
    title: "Chargement de votre environnement…",
    hint: "Préparation d'Aurélia Mail",
  },
  webmail: {
    title: "Ouverture du webmail…",
    hint: "Connexion à vos comptes",
  },
  messages: {
    title: "Chargement de vos messages…",
    hint: "Récupération depuis votre boîte mail",
  },
  search: {
    title: "Recherche IMAP en cours…",
    hint: "Interrogation du serveur dans toute la boîte",
  },
  folders: {
    title: "Chargement des dossiers…",
    hint: "Structure de votre boîte mail",
  },
  message: {
    title: "Ouverture du message…",
  },
  sync: {
    title: "Synchronisation…",
    hint: "Mise à jour en arrière-plan",
  },
};

type Props = {
  variant?: MailLoadingVariant;
  label?: string;
  hint?: string;
  /** full = écran centré · compact = liste · bar = fine barre seule · inline = spinner + texte en ligne */
  layout?: "full" | "compact" | "bar" | "inline";
  progress?: number;
  className?: string;
};

export function MailLoadingState({
  variant = "messages",
  label,
  hint,
  layout = "compact",
  progress,
  className,
}: Props) {
  const preset = VARIANT_COPY[variant];
  const title = label ?? preset.title;
  const subtitle = hint ?? preset.hint;

  if (layout === "bar") {
    return (
      <div
        className={cn("mx-3 my-2", className)}
        role="status"
        aria-live="polite"
        aria-label={title}
      >
        <div className="h-0.5 overflow-hidden rounded-full bg-[var(--mail-border-soft)]">
          <div className="inbox-load-progress-indeterminate h-full w-2/5 rounded-full bg-[var(--mail-accent)]" />
        </div>
      </div>
    );
  }

  if (layout === "inline") {
    return (
      <div
        className={cn("inline-flex items-center gap-2 text-[var(--mail-muted)]", className)}
        role="status"
        aria-live="polite"
        aria-label={title}
      >
        <MailCircularProgress size={18} className="shrink-0 [&_p]:hidden" />
        <span className="text-xs font-medium">{title}</span>
      </div>
    );
  }

  const size = layout === "full" ? 56 : 44;

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        layout === "full" ? "gap-4 px-6 py-16" : "gap-3 px-4 py-10",
        className
      )}
      role="status"
      aria-live="polite"
      aria-label={title}
    >
      <MailCircularProgress size={size} progress={progress} />
      <div className="flex max-w-xs flex-col gap-1">
        <p className="text-sm font-medium text-[var(--mail-text)]">{title}</p>
        {subtitle ? (
          <p className="text-xs leading-relaxed text-[var(--mail-muted)]">{subtitle}</p>
        ) : null}
      </div>
      {layout === "full" && progress === undefined ? (
        <div
          className="mt-1 h-0.5 w-40 overflow-hidden rounded-full bg-[var(--mail-border-soft)]"
          aria-hidden
        >
          <div className="inbox-load-progress-indeterminate h-full w-2/5 rounded-full bg-[var(--mail-accent)]" />
        </div>
      ) : null}
    </div>
  );
}
